import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Feature } from '@/services/backstage/Feature';
import { ApprovalState, useCancelWorkingPatternMutation, useGetMeQuery } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { useWorkingPatternRequests } from '@/services/workingPatterns/useWorkingPatternRequests';
import { FunctionComponent, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { ScheduleChangeRequestDetailsCard } from '../../../team/teamMember/requests/TeamMemberScheduleChangesDetailsCard';

const MyScheduleChangeRequestDetails: FunctionComponent = () => {
  const { requestId } = useParams<'requestId'>();
  const navigate = useNavigate();
  const [{ data: meData }] = useGetMeQuery();
  const employeeId = meData?.myEmployee?.identityId;
  const { request, refetch } = useWorkingPatternRequests(employeeId, requestId);
  const [cancelResult, cancelWorkingPattern] = useCancelWorkingPatternMutation();
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const isPending = request?.state === ApprovalState.Requested;

  const withdrawRequest = async () => {
    if (!employeeId || !isPending) return;

    const result = await cancelWorkingPattern({
      workingPatternCancellation: { employeeId }
    });

    await handleMutationResult(result, {
      successMessage: 'Schedule request withdrawn.',
      onSuccess: async () => {
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate('/ess/my-schedule'), 100);
      }
    });
  };

  if (!request) {
    return <p className="p-6">Schedule update request not found.</p>;
  }

  return (
    <Feature name="requests">
      <PageContainer>
        <PageSizer mode={SizerMode.PAGE} className="justify-items-center">
          <TSTitleCard
            className="w-full space-y-5 p-5"
            title={strings.timeshift.teamMember.requests.scheduleUpdate.cardTitle}
            icon={FiBell}
          >
            <ScheduleChangeRequestDetailsCard
              request={request}
              actions={
                <div className="flex justify-end space-x-3">
                  <Button big onClick={() => navigate(-1)}>
                    {strings.common.back}
                  </Button>
                  {isPending && (
                    <Button big danger onClick={() => setWithdrawDialogOpen(true)} disabled={cancelResult.fetching}>
                      Withdraw
                    </Button>
                  )}
                </div>
              }
            />
          </TSTitleCard>
        </PageSizer>
      </PageContainer>
      <ConfirmDialog
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
        onConfirm={withdrawRequest}
        title="Withdraw schedule request"
        confirmLabel="Withdraw"
        cancelLabel={strings.common.cancel}
        dangerous
        loading={cancelResult.fetching}
      >
        Are you sure you want to withdraw this schedule request?
      </ConfirmDialog>
    </Feature>
  );
};

export default MyScheduleChangeRequestDetails;
